import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { CircularProgress } from '@material-ui/core'
import { ReactFlow, Background, useNodesState, useEdgesState } from '@xyflow/react'
import '@xyflow/react/dist/style.css'

import { usePipelineDataContextWithVariables } from '../../provider/DDPDataProvider'
import useFlowGraphData from './hooks/useFlowGraphData'
import { applyLayout } from './layout'
import FlowGraphCard from './nodes/FlowGraphCard'
import FlowGraphBanner from './nodes/FlowGraphBanner'
import FlowGraphPill from './nodes/FlowGraphPill'
import FlowGraphCircle from './nodes/FlowGraphCircle'
import FlowGraphDiamond from './nodes/FlowGraphDiamond'
import FlowGraphMetric from './nodes/FlowGraphMetric'
import LabeledEdge from './edges/LabeledEdge'
import FlowGraphDetailPanel from './FlowGraphDetailPanel'

const NODE_TYPES = {
  flowGraphCard: FlowGraphCard,
  banner: FlowGraphBanner,
  pill: FlowGraphPill,
  circle: FlowGraphCircle,
  diamond: FlowGraphDiamond,
  metric: FlowGraphMetric
}
const EDGE_TYPES = { labeled: LabeledEdge }
const EMPTY_LAYOUT_OPTIONS = {}
const EMPTY_REACT_FLOW_PROPS = {}
const DEFAULT_EDGE_OPTIONS = { type: 'smoothstep' }

const FieldConfigShape = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.shape({
    accessor: PropTypes.string.isRequired,
    prefix: PropTypes.string,
    format: PropTypes.string,
    className: PropTypes.string,
    style: PropTypes.object
  })
])

const DetailFieldShape = PropTypes.shape({
  accessor: PropTypes.string.isRequired,
  header: PropTypes.string,
  format: PropTypes.string
})

const ColorOverrideShape = PropTypes.shape({
  background: PropTypes.string,
  text: PropTypes.string,
  border: PropTypes.string,
  selectedBorder: PropTypes.string
})

const LevelShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  idAccessor: PropTypes.string,
  match: PropTypes.string,
  nodeType: PropTypes.oneOf(['card', 'banner', 'pill', 'circle', 'diamond', 'metric']),
  name: FieldConfigShape,
  description: FieldConfigShape,
  variant: PropTypes.string,
  color: ColorOverrideShape,
  size: PropTypes.oneOf(['lg', 'md', 'sm']),
  detailFields: PropTypes.arrayOf(DetailFieldShape)
})

const EdgeLabelShape = PropTypes.shape({
  from: PropTypes.string.isRequired,
  to: PropTypes.string.isRequired,
  accessor: PropTypes.string.isRequired,
  format: PropTypes.string
})

function DDPFlowGraph ({
  pipeline,
  variables,

  dataMode,
  dataAccessor,
  levels,
  edgeLabels,
  groupingAccessor = 'grouping',
  childrenAccessor = 'data',

  nodesAccessor = 'nodes',
  edgesAccessor = 'edges',

  nodeIdAccessor = 'id',
  nodeNameAccessor = 'name',
  nodeDescriptionAccessor,
  nodeImageAccessor,

  edgeSourceAccessor = 'source',
  edgeTargetAccessor = 'target',
  edgeLabelAccessor,

  layout = 'hierarchical',
  layoutDirection = 'TB',
  layoutOptions,

  height = '500px',
  fitView = true,
  includeBackground = true,
  showDetailPanel = true,
  reactFlowProps,

  onNodeClick: onNodeClickProp,

  ...restProps
}) {
  const resolvedLayoutOptions = layoutOptions || EMPTY_LAYOUT_OPTIONS
  const resolvedReactFlowProps = reactFlowProps || EMPTY_REACT_FLOW_PROPS

  const { data, isLoading, error } = usePipelineDataContextWithVariables(pipeline, variables)

  const { nodes: rawNodes, edges: rawEdges } = useFlowGraphData(data, {
    dataMode,
    dataAccessor,
    levels,
    edgeLabels,
    groupingAccessor,
    childrenAccessor,
    nodesAccessor,
    edgesAccessor,
    nodeIdAccessor,
    nodeNameAccessor,
    nodeDescriptionAccessor,
    nodeImageAccessor,
    edgeSourceAccessor,
    edgeTargetAccessor,
    edgeLabelAccessor
  })

  const layoutedNodes = useMemo(() => {
    if (!rawNodes.length) return rawNodes
    return applyLayout(rawNodes, rawEdges, layout, {
      direction: layoutDirection,
      ...resolvedLayoutOptions
    })
  }, [rawNodes, rawEdges, layout, layoutDirection, resolvedLayoutOptions])

  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])

  const prevLayoutRef = useRef(null)

  useEffect(() => {
    const nodeKey = layoutedNodes.map(n => `${n.id}:${n.position.x},${n.position.y}`).join('|')
    const edgeKey = rawEdges.map(e => `${e.id}:${e.source}-${e.target}`).join('|')
    const key = `${nodeKey}::${edgeKey}`
    if (prevLayoutRef.current === key) return
    prevLayoutRef.current = key

    setNodes(layoutedNodes)
    setEdges(rawEdges)
  }, [layoutedNodes, rawEdges, setNodes, setEdges])

  const [selectedNode, setSelectedNode] = useState(null)

  const onNodeClick = useCallback((_event, node) => {
    if (onNodeClickProp) {
      onNodeClickProp(node)
    }
    if (showDetailPanel) {
      setSelectedNode(prev => prev?.id === node.id ? null : node)
    }
  }, [onNodeClickProp, showDetailPanel])

  const onPaneClick = useCallback(() => {
    setSelectedNode(null)
  }, [])

  if (isLoading) {
    return (
      <FlowGraphStatus height={height}>
        <CircularProgress size={32} />
      </FlowGraphStatus>
    )
  }

  if (error) {
    return (
      <FlowGraphStatus height={height}>
        <span style={{ color: 'var(--color-danger-500)', fontSize: '0.875rem' }}>
          Failed to load graph data
        </span>
      </FlowGraphStatus>
    )
  }

  if (!layoutedNodes.length) {
    return (
      <FlowGraphStatus height={height}>
        <span style={{ color: 'var(--text-tertiary)', fontSize: '0.875rem' }}>No data to display</span>
      </FlowGraphStatus>
    )
  }

  return (
    <div {...restProps} style={{ ...restProps.style, height, width: '100%', position: 'relative' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={NODE_TYPES}
        edgeTypes={EDGE_TYPES}
        defaultEdgeOptions={DEFAULT_EDGE_OPTIONS}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        fitView={fitView}
        proOptions={{ hideAttribution: true }}
        panOnDrag
        zoomOnScroll
        preventScrolling={false}
        nodesDraggable
        nodesConnectable={false}
        edgesFocusable={false}
        {...resolvedReactFlowProps}
      >
        {includeBackground && <Background color='var(--color-neutral-200)' gap={20} />}
      </ReactFlow>

      {showDetailPanel && (
        <FlowGraphDetailPanel
          node={selectedNode}
          open={!!selectedNode}
          onClose={() => setSelectedNode(null)}
          levels={levels}
        />
      )}
    </div>
  )
}

function FlowGraphStatus ({ height, children }) {
  return (
    <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {children}
    </div>
  )
}

FlowGraphStatus.propTypes = {
  height: PropTypes.string,
  children: PropTypes.node
}

DDPFlowGraph.propTypes = {
  pipeline: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  variables: PropTypes.object,

  dataMode: PropTypes.oneOf(['structured', 'relational', 'flat', 'tree']),
  dataAccessor: PropTypes.string,
  levels: PropTypes.arrayOf(LevelShape),
  edgeLabels: PropTypes.arrayOf(EdgeLabelShape),
  groupingAccessor: PropTypes.string,
  childrenAccessor: PropTypes.string,

  nodesAccessor: PropTypes.string,
  edgesAccessor: PropTypes.string,

  nodeIdAccessor: PropTypes.string,
  nodeNameAccessor: PropTypes.string,
  nodeDescriptionAccessor: PropTypes.string,
  nodeImageAccessor: PropTypes.string,

  edgeSourceAccessor: PropTypes.string,
  edgeTargetAccessor: PropTypes.string,
  edgeLabelAccessor: PropTypes.string,

  layout: PropTypes.oneOf(['hierarchical', 'radial', 'force', 'freeform']),
  layoutDirection: PropTypes.oneOf(['TB', 'BT', 'LR', 'RL']),
  layoutOptions: PropTypes.object,

  height: PropTypes.string,
  fitView: PropTypes.bool,
  includeBackground: PropTypes.bool,
  showDetailPanel: PropTypes.bool,
  reactFlowProps: PropTypes.object,

  onNodeClick: PropTypes.func
}

DDPFlowGraph.defaultProps = {
  groupingAccessor: 'grouping',
  childrenAccessor: 'data',
  nodesAccessor: 'nodes',
  edgesAccessor: 'edges',
  nodeIdAccessor: 'id',
  nodeNameAccessor: 'name',
  edgeSourceAccessor: 'source',
  edgeTargetAccessor: 'target',
  layout: 'hierarchical',
  layoutDirection: 'TB',
  height: '500px',
  fitView: true,
  includeBackground: true,
  showDetailPanel: true
}

export default DDPFlowGraph
